'use client'

import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { Mail, CheckCircle, AlertTriangle } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useAuth } from '@/contexts/auth-context'
import { captureEvent } from '@/instrumentation-client'
import { isAppleSignInEnabled, isGoogleSignInEnabled } from '@/lib/feature-flags'
import { useAppleWebAuthAvailability } from '@/lib/apple-web-auth'
import { useNativeAppleAuthAvailability } from '@/lib/native-apple-auth'
import { useGoogleSignInNativeBlocker } from '@/lib/native-google-auth'
import { reportSocialSignInFailure } from '@/components/auth/social-sign-in-failure'
import { cn } from '@/lib/utils'
import { rememberPendingCharacter } from '@/lib/pending-character'
import {
  getAuthVariantStyle,
  getDefaultBackHref,
  type AuthVariant,
} from '@/components/auth/auth-variant-styles'
import { useCapacitorBridge } from '@/components/capacitor-bridge-provider'
import { type SocialProvider, type SocialSignInButton, SocialSignInControls } from '@/components/auth/social-sign-in-controls'
import { ReferralCodeField } from '@/components/auth/referral-code-field'
import type { AuthChallengeMethod } from '@/server/domain/clients'
import {
  getGenericLandingHrefBySegment,
} from '@/lib/generic-landings/paths'
import {
  surfaceForSegment,
  surfaceHref,
  type LocaleSurface,
} from '@/lib/locale-capabilities'
import { servesCustomCharacterLanding } from '@/lib/feature-flags'
import { rememberMarketingConsent } from '@/lib/pending-signup'

const OTP_LENGTH = 6
const COOLDOWN_SECONDS = 60

const SOCIAL_AUTH_CONFIG = {
  apple: {
    startedEvent: 'auth_apple_started',
    failedEvent: 'auth_apple_failed',
    errorKey: 'apple.error',
    noAccountKey: 'apple.noAccount'
  },
  google: {
    startedEvent: 'auth_google_started',
    failedEvent: 'auth_google_failed',
    errorKey: 'google.error',
    noAccountKey: 'google.noAccount'
  }
} as const

interface AuthScreenProps {
  variant?: AuthVariant
  locale?: string
  backHref?: string
  resolveBackHrefFromQuery?: boolean
}

function canShowGoogleSignIn(isBlockedInNativeRuntime: boolean): boolean {
  if (isBlockedInNativeRuntime) return false
  return isGoogleSignInEnabled()
}

function getSocialAuthConfig(provider: SocialProvider) {
  return SOCIAL_AUTH_CONFIG[provider]
}

/**
 * Superficies con `?from=` válido en el auth: cada una declara su interruptor
 * de despliegue (o «siempre»). El resolver es genérico (surfaceForSegment +
 * surfaceHref de lib/locale-capabilities); el gate de rollout es de quien lo
 * consume — aquí —, no del registro de superficies.
 */
const BACK_LINK_SURFACE_ENABLED: Partial<Record<LocaleSurface, () => boolean>> = {
  statistics: () => true,
  'custom-landing': servesCustomCharacterLanding,
}

function resolveAuthBackHref({
  resolveFromQuery,
  fromSegment,
  locale,
  backHref,
  defaultBackHref,
}: {
  resolveFromQuery: boolean
  fromSegment?: string
  locale: string
  backHref?: string
  defaultBackHref: string
}): string {
  if (!resolveFromQuery) return backHref ?? defaultBackHref
  // Dos familias de segmentos: el catálogo genérico (registro cerrado en
  // lib/generic-landings/paths) y las superficies fuera de él, como
  // /tu-personaje (segmento → superficie → href, con su gate de despliegue).
  const surface = surfaceForSegment(fromSegment)
  const surfaceBackHref =
    surface && (BACK_LINK_SURFACE_ENABLED[surface]?.() ?? false)
      ? surfaceHref(surface, locale)
      : undefined
  return (
    getGenericLandingHrefBySegment(fromSegment, locale) ??
    surfaceBackHref ??
    backHref ??
    defaultBackHref
  )
}

// The keys this helper resolves. Narrowing to these (rather than `string`) lets
// a strictly-typed next-intl translator be passed directly: a translator over a
// wider key domain is assignable to a function asking for fewer keys.
type SocialSignInTranslationKey =
  | 'apple.button'
  | 'apple.loading'
  | 'google.button'
  | 'google.loading'

interface BuildSocialSignInButtonsInput {
  appleSignInEnabled: boolean
  googleSignInEnabled: boolean
  translate: (key: SocialSignInTranslationKey) => string
  onSocialSignIn: (provider: SocialProvider) => void
}

function buildSocialSignInButtons({
  appleSignInEnabled,
  googleSignInEnabled,
  translate,
  onSocialSignIn
}: BuildSocialSignInButtonsInput): SocialSignInButton[] {
  const buttons: SocialSignInButton[] = []

  if (appleSignInEnabled) {
    buttons.push({
      provider: 'apple',
      label: translate('apple.button'),
      loadingLabel: translate('apple.loading'),
      onClick: () => onSocialSignIn('apple')
    })
  }

  if (googleSignInEnabled) {
    buttons.push({
      provider: 'google',
      label: translate('google.button'),
      loadingLabel: translate('google.loading'),
      onClick: () => onSocialSignIn('google')
    })
  }

  return buttons
}

export function AuthScreen({
  variant = 'santa',
  locale,
  backHref,
  resolveBackHrefFromQuery = false,
}: AuthScreenProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const params = useParams()
  const { isNativeApp } = useCapacitorBridge()
  const nativeAppleSignInEnabled = useNativeAppleAuthAvailability(isNativeApp)
  const appleWebAuthAvailable = useAppleWebAuthAvailability(isNativeApp)
  const appleSignInEnabled = nativeAppleSignInEnabled
    || (appleWebAuthAvailable && isAppleSignInEnabled())
  const googleSignInBlockedInNativeRuntime = useGoogleSignInNativeBlocker(isNativeApp)
  const resolvedLocale = locale ?? (params?.locale as string | undefined) ?? 'es'
  const { sendOtp, verifyOtp, signInWithApple, signInWithGoogle, isAuthenticated, isLoading: authLoading } = useAuth()
  const style = getAuthVariantStyle(variant)
  const t = useTranslations(style.namespace)
  const tCommon = useTranslations('common')

  const [step, setStep] = useState<'email' | 'otp'>('email')
  // Casilla de marketing (ADR-047): estado de React puro, desmarcada al montar.
  // El carrier del sync se escribe con ESTE valor al iniciar cada intento de
  // acceso (`startAuthAttempt`), así que lo que viaja es siempre lo que había
  // en pantalla, y un intento abandonado no se hereda.
  const [marketingConsent, setMarketingConsent] = useState(false)
  const startAuthAttempt = () => rememberMarketingConsent(marketingConsent)
  const [email, setEmail] = useState('')
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''))
  const [reusableCode, setReusableCode] = useState('')
  const [verificationMethod, setVerificationMethod] = useState<AuthChallengeMethod>('email_otp')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submittingSocialProvider, setSubmittingSocialProvider] = useState<SocialProvider | null>(null)
  const [showDeletedMessage, setShowDeletedMessage] = useState(false)
  const [cooldownSeconds, setCooldownSeconds] = useState(0)
  const [hasResendError, setHasResendError] = useState(false)
  const [hasSentEmailSuccessfully, setHasSentEmailSuccessfully] = useState(false)

  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const reusableCodeRef = useRef<HTMLInputElement | null>(null)
  const cooldownIntervalRef = useRef<NodeJS.Timeout | null>(null)

  const defaultBackHref = getDefaultBackHref(variant, resolvedLocale, isNativeApp)
  const resolvedBackHref = resolveAuthBackHref({
    resolveFromQuery: resolveBackHrefFromQuery,
    fromSegment: searchParams.get('from') ?? undefined,
    locale: resolvedLocale,
    backHref,
    defaultBackHref,
  })
  const googleSignInEnabled = canShowGoogleSignIn(googleSignInBlockedInNativeRuntime)
  const socialSignInByProvider = {
    apple: signInWithApple,
    google: signInWithGoogle
  } satisfies Record<SocialProvider, (locale?: string) => Promise<{ success: boolean; error?: string }>>
  const isReusableCode = verificationMethod === 'reusable_code'

  const cardClassName = cn(
    'w-full max-w-md rounded-2xl p-8 backdrop-blur-sm shadow-xl font-standard',
    style.card
  )
  const primaryButtonClassName = cn(
    'w-full rounded-lg px-6 py-3 font-bold shadow-lg transition-all hover:scale-105 hover:shadow-xl active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed',
    style.primaryButton
  )
  const resendButtonClassName = cn(
    'text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
    style.resendButton
  )

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      router.replace('/dashboard')
    }
  }, [authLoading, isAuthenticated, router])

  // Las landings públicas pasan `?character=<id>` con el personaje que el usuario
  // venía buscando. Se guarda en cookie porque el login social sale de la app y
  // vuelve por /auth/callback: el param de esta URL no sobrevive ese salto. El
  // dashboard lo consume para abrir resaltando esa card.
  useEffect(() => {
    rememberPendingCharacter(searchParams.get('character'))
  }, [searchParams])

  const hasDeletedParam = searchParams.get('deleted') === 'true'
  const hasErrorParam = Boolean(searchParams.get('error') || searchParams.get('error_description'))
  const deletedHandledRef = useRef(false)
  const errorHandledRef = useRef(false)

  useEffect(() => {
    if (hasDeletedParam && !deletedHandledRef.current) {
      deletedHandledRef.current = true
      setShowDeletedMessage(true)
      const timer = setTimeout(() => setShowDeletedMessage(false), 5000)
      return () => clearTimeout(timer)
    }
  }, [hasDeletedParam])

  useEffect(() => {
    if (hasErrorParam && !errorHandledRef.current) {
      errorHandledRef.current = true
      setError(t('google.error'))
      captureEvent('auth_google_failed', {
        success: false,
        locale: resolvedLocale,
        variant,
        error: searchParams.get('error') || 'oauth_return_error'
      })
    }
  }, [hasErrorParam, resolvedLocale, searchParams, t, variant])

  useEffect(() => {
    if (step === 'otp') {
      const focusTimer = setTimeout(() => {
        if (isReusableCode) {
          reusableCodeRef.current?.focus()
        } else {
          inputRefs.current[0]?.focus()
        }
      }, 100)
      return () => clearTimeout(focusTimer)
    }
  }, [isReusableCode, step])

  // El intervalo se arma cuando el cooldown ARRANCA y se desarma cuando llega a
  // cero; no debe recrearse en cada tick. Por eso la dependencia es el booleano
  // y no el contador — extraído a una variable porque una expresión inline en
  // el array de dependencias ESLint no puede comprobarla estáticamente.
  const isCoolingDown = cooldownSeconds > 0

  useEffect(() => {
    if (isCoolingDown) {
      cooldownIntervalRef.current = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            if (cooldownIntervalRef.current) {
              clearInterval(cooldownIntervalRef.current)
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current)
      }
    }
  }, [isCoolingDown])

  const startCooldown = () => {
    setCooldownSeconds(COOLDOWN_SECONDS)
  }

  const handleOtpChange = (index: number, value: string) => {
    const digitsOnly = value.replace(/\D/g, '')
    // iOS "from email" autofill injects the full OTP into the first input and fires onChange (no paste event)
    if (digitsOnly.length > 1) {
      const spread = digitsOnly.slice(0, OTP_LENGTH).split('')
      const newOtpDigits = [...otpDigits]
      spread.forEach((d, i) => { newOtpDigits[i] = d })
      setOtpDigits(newOtpDigits)
      const focusIndex = Math.min(spread.length, OTP_LENGTH) - 1
      inputRefs.current[focusIndex]?.focus()
      return
    }
    const digit = digitsOnly.slice(-1)
    const newOtpDigits = [...otpDigits]
    newOtpDigits[index] = digit
    setOtpDigits(newOtpDigits)
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }

    if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)

    if (pastedData) {
      const newOtpDigits = [...otpDigits]
      for (let i = 0; i < pastedData.length; i++) {
        newOtpDigits[i] = pastedData[i]
      }
      setOtpDigits(newOtpDigits)

      const nextEmptyIndex = newOtpDigits.findIndex(d => !d)
      const focusIndex = nextEmptyIndex === -1 ? OTP_LENGTH - 1 : nextEmptyIndex
      inputRefs.current[focusIndex]?.focus()
    }
  }

  const getOtpString = () => otpDigits.join('')
  const getVerificationCode = () => isReusableCode ? reusableCode : getOtpString()

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()

    if (cooldownSeconds > 0) {
      return
    }

    setError('')
    setHasResendError(false)
    setHasSentEmailSuccessfully(false)
    setIsSubmitting(true)
    startAuthAttempt()

    const result = await sendOtp(email, resolvedLocale)

    if (result.success) {
      captureEvent('auth_otp_requested', { email: email, success: true, locale: resolvedLocale })

      const method = result.verificationMethod ?? 'email_otp'
      setVerificationMethod(method)
      setReusableCode('')
      setOtpDigits(Array(OTP_LENGTH).fill(''))
      if (method === 'email_otp') {
        startCooldown()
        setHasSentEmailSuccessfully(true)
      }
      setStep('otp')
    } else {
      captureEvent('auth_otp_requested', {
        email: email,
        success: false,
        error: result.error || t('errors.sendOtp'),
        locale: resolvedLocale
      })

      setError(result.error || t('errors.sendOtp'))
    }

    setIsSubmitting(false)
  }

  const handleSocialSignIn = async (provider: SocialProvider) => {
    const config = getSocialAuthConfig(provider)
    const signIn = socialSignInByProvider[provider]

    setError('')
    setIsSubmitting(true)
    setSubmittingSocialProvider(provider)
    captureEvent(config.startedEvent, {
      success: true,
      locale: resolvedLocale,
      variant
    })
    startAuthAttempt()

    const result = await signIn(resolvedLocale)

    if (!result.success) {
      // Decide en silencio si esto merece banner y evento: cancelar no lo merece.
      reportSocialSignInFailure({
        result,
        config,
        translate: t,
        locale: resolvedLocale,
        variant,
        showError: setError
      })
      setIsSubmitting(false)
      setSubmittingSocialProvider(null)
    }
  }

  const handleResendOtp = async () => {
    if (cooldownSeconds > 0) {
      return
    }

    setError('')
    setHasResendError(false)
    setIsSubmitting(true)
    startAuthAttempt()

    const result = await sendOtp(email, resolvedLocale)

    if (result.success) {
      captureEvent('auth_otp_resent', { email: email, success: true, locale: resolvedLocale })

      startCooldown()
      setOtpDigits(Array(OTP_LENGTH).fill(''))
      inputRefs.current[0]?.focus()
    } else {
      captureEvent('auth_otp_resent', {
        email: email,
        success: false,
        error: result.error || t('errors.resendOtp'),
        locale: resolvedLocale
      })

      setError(result.error || t('errors.resendOtp'))
      setHasResendError(true)
    }

    setIsSubmitting(false)
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsSubmitting(true)

    const result = await verifyOtp(email, getVerificationCode(), resolvedLocale, verificationMethod)

    if (result.success) {
      router.push('/dashboard')
    } else {
      captureEvent('auth_otp_verified', {
        email: email,
        success: false,
        error: result.error || t('errors.invalidOtp'),
        locale: resolvedLocale
      })

      setError(result.error || t('errors.invalidOtp'))
    }

    setIsSubmitting(false)
  }

  const { decorations, footerIcons } = style
  const socialSignInButtons = buildSocialSignInButtons({
    appleSignInEnabled,
    googleSignInEnabled,
    translate: t,
    onSocialSignIn: handleSocialSignIn
  })

  return (
    <div
      className={cn(
        'readable-body flex min-h-screen flex-col items-center justify-center p-6 relative',
        style.pageGradient
      )}
    >
      <div className="w-full max-w-md mb-4">
        <Link
          href={resolvedBackHref}
          className={cn(
            'inline-flex items-center gap-2 text-sm transition-colors',
            style.backLink
          )}
        >
          <span aria-hidden>←</span>
          <span>{t('back')}</span>
        </Link>
      </div>

      {decorations.map((decoration, index) => (
        <div key={`${decoration.content}-${index}`} className={decoration.className} style={decoration.style}>
          {decoration.content}
        </div>
      ))}

      <h1 className={cn('mb-2 text-center text-4xl font-bold md:text-5xl font-christmas', style.onDark ? 'text-white' : 'text-foreground')}>
        {t('title')}
      </h1>
      <p className={cn('mb-8 text-center text-lg', style.onDark ? 'text-white/80' : 'text-muted-foreground')}>
        {t('subtitle')}
      </p>

      {showDeletedMessage && (
        <div className="w-full max-w-md mb-6 rounded-lg bg-green-100 dark:bg-green-900 border-2 border-green-500 dark:border-green-700 p-4 shadow-lg">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400 flex-shrink-0" />
            <div>
              <h3 className="font-christmas font-semibold text-green-800 dark:text-green-100">
                {t('accountDeleted.title')}
              </h3>
              <p className="text-sm text-green-700 dark:text-green-200">
                {t('accountDeleted.message')}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className={cardClassName}>
        {step === 'email' ? (
          <form onSubmit={handleSendOtp} className="space-y-6">
            <SocialSignInControls
              buttons={socialSignInButtons}
              isSubmitting={isSubmitting}
              isSpecialTheme={style.onDark}
              submittingProvider={submittingSocialProvider}
              variant={variant}
              separatorLabel={t('email.separator')}
            />

            <div>
              <label htmlFor="email" className={cn('block text-base font-medium mb-2', style.onDark ? 'text-white' : 'text-foreground')}>
                {t('email.label')}
              </label>
              <p className={cn('text-base mb-3', style.onDark ? 'text-white/70' : 'text-muted-foreground')}>
                {t('email.description')}
              </p>
              <p className={cn('text-sm mb-3 font-standard', style.onDark ? 'text-white/80' : 'text-muted-foreground')}>
                {t('email.preInfo')}
              </p>
              <div className="relative">
                <Mail className={cn('absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5', style.onDark ? 'text-white/70' : 'text-muted-foreground')} />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('email.placeholder')}
                  required
                  className={cn(
                    'w-full rounded-lg border border-input bg-background pl-10 pr-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent',
                    style.emailInput
                  )}
                />
              </div>
            </div>

            {error && (
              <div
                className={cn(
                  'rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-base',
                  style.onDark ? 'text-white' : 'text-destructive'
                )}
              >
                {error}
              </div>
            )}

            {hasSentEmailSuccessfully && !error && (
              <div
                className={cn(
                  'rounded-lg p-3 text-sm flex gap-2 items-start shadow-sm',
                  style.onDark
                    ? 'bg-white/10 text-white border border-white/40'
                    : 'border border-amber-300/70 bg-amber-50/90 text-amber-900 dark:bg-amber-900/40 dark:text-amber-50 dark:border-amber-400/60'
                )}
              >
                <span aria-hidden className="text-base">⚠️</span>
                <p className="font-standard">
                  {t('email.postSendInfo')}
                </p>
              </div>
            )}

            {/* Canje manual del código de invitación (AC2.6, camino primario nativo):
                escribe el carrier que attemptAccountSync adjunta al sync. Solo con
                NEXT_PUBLIC_REFERRAL_ENABLED (el propio campo se auto-oculta). El hint
                de la oferta lo interpola next-intl con el P real del programa. */}
            <ReferralCodeField
              onDarkBackground={style.onDark}
              translations={{
                toggle: tCommon('referralCode.toggle'),
                label: tCommon('referralCode.label'),
                placeholder: tCommon('referralCode.placeholder'),
                formatPurchaseBonusHint: (minutes) =>
                  tCommon('referralCode.purchaseBonusHint', { minutes }),
              }}
            />

            {/* Casilla de marketing (ADR-047): desmarcada por defecto y ANTES del
                envío, para que el teclado y los lectores de pantalla la encuentren
                antes de la acción que la compromete. El servidor solo la sella si
                el sync crea la cuenta. */}
            <label
              className={cn(
                'flex items-start gap-2 text-sm text-left',
                style.onDark ? 'text-white/80' : 'text-muted-foreground'
              )}
            >
              <input
                type="checkbox"
                className="mt-0.5"
                checked={marketingConsent}
                onChange={(event) => setMarketingConsent(event.target.checked)}
              />
              <span>{tCommon('marketingConsent.signup')}</span>
            </label>

            <button
              type="submit"
              disabled={isSubmitting || cooldownSeconds > 0}
              className={primaryButtonClassName}
            >
              {isSubmitting
                ? t('email.buttonSending')
                : cooldownSeconds > 0
                  ? t('email.buttonCooldown', { seconds: cooldownSeconds })
                  : t('email.button')
              }
            </button>

            <p className={cn('text-sm text-center', style.onDark ? 'text-white/70' : 'text-muted-foreground')}>
              {t('legal.text')}{' '}
              <a href={`/${resolvedLocale}/terms`} className={cn('hover:underline', style.onDark ? 'text-white' : 'text-foreground')}>
                {t('legal.terms')}
              </a>
              {' '}{t('legal.and')}{' '}
              <a href={`/${resolvedLocale}/privacy`} className={cn('hover:underline', style.onDark ? 'text-white' : 'text-foreground')}>
                {t('legal.privacy')}
              </a>
              .
            </p>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-6">
            <div>
              <label className={cn('block text-base font-medium mb-2', style.onDark ? 'text-white' : 'text-foreground')}>
                {t('otp.label')}
              </label>
              <p className={cn('text-base mb-4', style.onDark ? 'text-white/70' : 'text-muted-foreground')}>
                {isReusableCode
                  ? t('otp.reviewDescription', { email })
                  : t('otp.description', { email })}
              </p>
              {!isReusableCode && (
                <div
                  className={cn(
                    'rounded-lg p-3 text-sm flex gap-2 items-start shadow-sm mb-4',
                    style.onDark
                      ? 'bg-white/10 text-white border border-white/40'
                      : 'border border-amber-300/70 bg-amber-50/90 text-amber-900 dark:bg-amber-900/40 dark:text-amber-50 dark:border-amber-400/60'
                  )}
                >
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden />
                  <p className="font-standard">
                    {t('otp.helpSpam')}
                  </p>
                </div>
              )}
              {isReusableCode ? (
                <input
                  ref={reusableCodeRef}
                  type="text"
                  value={reusableCode}
                  onChange={(event) => setReusableCode(event.target.value.trim())}
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  disabled={isSubmitting}
                  className={cn(
                    'w-full rounded-lg border border-input bg-background px-4 py-3 text-center text-xl font-bold tracking-widest text-foreground focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed',
                    style.codeInput
                  )}
                  aria-label={t('otp.reviewCodeLabel')}
                />
              ) : (
                <div className="flex justify-center gap-2 sm:gap-3">
                  {otpDigits.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => { inputRefs.current[index] = el }}
                    type="text"
                    inputMode="numeric"
                    autoComplete={index === 0 ? 'one-time-code' : 'off'}
                    maxLength={index === 0 ? OTP_LENGTH : 1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onInput={index === 0 ? (e) => handleOtpChange(0, (e.target as HTMLInputElement).value) : undefined}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    disabled={isSubmitting}
                    className={cn(
                      'w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl font-bold rounded-lg border-2 border-input bg-background text-foreground focus:border-accent focus:ring-2 focus:ring-accent/30 outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed',
                      style.otpDigit
                    )}
                    aria-label={t('otp.digitLabel', { index: index + 1 })}
                  />
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div
                className={cn(
                  'rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-base',
                  style.onDark ? 'text-white' : 'text-destructive'
                )}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || (isReusableCode ? reusableCode.length === 0 : getOtpString().length !== OTP_LENGTH)}
              className={primaryButtonClassName}
            >
              {isSubmitting ? t('otp.buttonVerifying') : t('otp.button')}
            </button>

            <div className="flex flex-col items-center gap-2">
              {!isReusableCode && (
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isSubmitting || cooldownSeconds > 0}
                  className={resendButtonClassName}
                >
                  {cooldownSeconds > 0
                    ? t('otp.resendCooldown', { seconds: cooldownSeconds })
                    : t('otp.resend')
                  }
                </button>
              )}

              {!isReusableCode && hasResendError && (
                <p className={cn('text-sm text-center font-standard', style.onDark ? 'text-white/80' : 'text-muted-foreground')}>
                  {t('otp.noEmailHelp')}
                </p>
              )}

              <button
                type="button"
                onClick={() => {
                  setStep('email')
                  setOtpDigits(Array(OTP_LENGTH).fill(''))
                  setReusableCode('')
                  setVerificationMethod('email_otp')
                  setError('')
                }}
                className={cn('text-sm transition-colors', style.onDark ? 'text-white/70 hover:text-white' : 'text-muted-foreground hover:text-foreground')}
              >
                {t('otp.backToEmail')}
              </button>
            </div>

            <p className={cn('text-sm text-center', style.onDark ? 'text-white/70' : 'text-muted-foreground')}>
              {t('legal.text')}{' '}
              <a href={`/${resolvedLocale}/terms`} className={cn('hover:underline', style.onDark ? 'text-white' : 'text-foreground')}>
                {t('legal.terms')}
              </a>
              {' '}{t('legal.and')}{' '}
              <a href={`/${resolvedLocale}/privacy`} className={cn('hover:underline', style.onDark ? 'text-white' : 'text-foreground')}>
                {t('legal.privacy')}
              </a>
              .
            </p>
          </form>
        )}
      </div>

      <div className="absolute bottom-4 flex gap-4 text-3xl">
        {footerIcons.map((icon, index) => (
          <span key={`${icon}-${index}`}>{icon}</span>
        ))}
      </div>
    </div>
  )
}
